import { useEffect, useRef, useState } from "react";

/**
 * Apple-style scroll reveal: fades + translates content into view
 * once it crosses the viewport threshold. Uses native IntersectionObserver,
 * cubic-bezier(0.25, 1, 0.5, 1) for the signature Apple easing.
 *
 * Props:
 *   delay        - ms delay before the reveal starts (stagger control)
 *   y            - translateY in px to lift from (default 32)
 *   duration     - ms (default 900)
 *   threshold    - 0–1 visibility ratio to trigger (default 0.15)
 *   once         - if true, only animate the first time (default true)
 *   as           - element tag (default "div")
 */
export default function Reveal({
  children,
  delay = 0,
  y = 32,
  duration = 900,
  threshold = 0.15,
  once = true,
  as: Tag = "div",
  className = "",
  style = {},
  ...rest
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof window !== "undefined" && !("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) obs.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold, rootMargin: "0px 0px -12% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, once]);

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translate3d(0,0,0)" : `translate3d(0,${y}px,0)`,
        transition: `opacity ${duration}ms cubic-bezier(0.25, 1, 0.5, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.25, 1, 0.5, 1) ${delay}ms`,
        willChange: "opacity, transform",
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
